import { useState, useRef, useCallback } from "react";

/**
 * Before/After slider state + mouse/touch handlers for the Magic result view.
 * Returns sliderPos (0-100) + ref for the container + mouse/touch handlers.
 * Factored out of DoozyGoMagic.js — purely presentational.
 */
export default function useMagicSlider(initialPos = 50) {
  const [sliderPos, setSliderPos] = useState(initialPos);
  const sliderContainerRef = useRef(null);
  const isDragging = useRef(false);

  const handleSliderDrag = useCallback((clientX) => {
    const container = sliderContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(2, Math.min(98, (x / rect.width) * 100));
    setSliderPos(pct);
  }, []);

  const handleSliderMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    handleSliderDrag(e.clientX);
    const onMove = (ev) => { if (isDragging.current) handleSliderDrag(ev.clientX); };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [handleSliderDrag]);

  const handleSliderTouchStart = useCallback((e) => {
    isDragging.current = true;
    handleSliderDrag(e.touches[0].clientX);
    const onMove = (ev) => { if (isDragging.current) handleSliderDrag(ev.touches[0].clientX); };
    const onEnd = () => {
      isDragging.current = false;
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
  }, [handleSliderDrag]);

  return {
    sliderPos,
    setSliderPos,
    sliderContainerRef,
    handleSliderMouseDown,
    handleSliderTouchStart,
  };
}
